<script setup lang="ts">
import { onBeforeUnmount, onMounted } from "vue"
import { useI18n } from "vue-i18n"

const { t, locale } = useI18n({ useScope: "global" })

// Show the full-bleed home background image (see the rule in src/app.css) only while the home view is
// mounted by toggling the class on the document element, which carries the page background color.
const homeBackgroundClass = "razume-home-background"

onMounted(() => {
  document.documentElement.classList.add(homeBackgroundClass)
})

onBeforeUnmount(() => {
  document.documentElement.classList.remove(homeBackgroundClass)
})
</script>

<template>
  <div class="flex w-full flex-col justify-center">
    <!--
      flex-wrap drops the right logos below the left group when the two no longer fit side by side, which is what they do at every usual viewport width: the
      two groups stand side by side only from about 1830px up, while the funding text under the left logos fits on a single line from about 1255px up. The row
      therefore wraps long before the text has to, so whenever the two groups are side by side the left group is exactly h-12 plus gap-1 plus one text line,
      which is the height the right logos are fixed to. Once the two are stacked their sizes stop matching, and are not meant to: the left logos keep their
      aspect ratio inside the h-12 row, so as the row narrows they are drawn ever shorter than it (about a third of it at phone widths), while the right logos
      keep the height they are fixed to.
    -->
    <div class="flex flex-row flex-wrap items-center justify-center gap-x-8 gap-y-6 p-4 sm:gap-x-24 sm:p-12 md:gap-x-48 md:p-24">
      <div class="flex flex-col gap-1">
        <div class="flex h-12 flex-row gap-4 sm:gap-8 md:gap-12">
          <a id="home-link-mk" href="https://www.gov.si/drzavni-organi/ministrstva/ministrstvo-za-kulturo/" class="h-full"
            ><img id="home-icon-mk" :src="`/mk_${locale}.svg`" :alt="t('home.mk')" class="h-full"
          /></a>
          <a id="home-link-noo" href="https://www.gov.si/zbirke/projekti-in-programi/nacrt-za-okrevanje-in-odpornost/" class="h-full"
            ><img id="home-icon-noo" :src="`/noo_${locale}.svg`" alt="NOO" class="h-full"
          /></a>
          <a id="home-link-eu" href="https://next-generation-eu.europa.eu/" class="h-full"
            ><img id="home-icon-eu" :src="`/eu_${locale}.svg`" alt="NextGenerationEU" class="h-full"
          /></a>
        </div>
        <div id="home-text-funding" class="text-center text-neutral-500">{{ t("home.funding") }}</div>
      </div>
      <!--
        The height is the height of the left group while its funding text is on one line, which is the state the two groups are in whenever they are side by
        side. See the comment above.
      -->
      <div class="flex flex-row items-center gap-4 sm:gap-8 md:gap-12">
        <a id="home-link-ekultura" href="https://www.ekulturno.si/"
          ><img id="home-icon-ekultura" src="/ekultura_logo.svg" alt="eKultura" class="h-[calc(var(--spacing)*12_+_var(--spacing)*1_+_1lh)]"
        /></a>
        <a id="home-link-ean" href="https://european-art.net/database"
          ><img id="home-icon-ean" src="/ean_logo.png" alt="EAN" class="h-[calc(var(--spacing)*12_+_var(--spacing)*1_+_1lh)]"
        /></a>
      </div>
    </div>
  </div>
</template>
